import { useBilling } from '@hook'
import { InvoiceItemRes } from '@type/invoice.type'
import { Button, Modal } from '@ui'
import { getError, showToast } from '@util'
import { useEffect, useMemo, useState } from 'react'
import { itemHasDiscount, lineDiscount } from './ApplyDiscount'

type DiscountedItem = Pick<InvoiceItemRes, 'id'> & {
  attributes: Pick<
    InvoiceItemRes['attributes'],
    'category' | 'name' | 'unit_price' | 'quantity' | 'discount_type' | 'discount_value' | 'discount_reason' | 'status'
  >
}

type RemoveDiscountProps = {
  invoiceId: string
  items: DiscountedItem[]
  onClose: () => void
}

export const RemoveDiscount = ({ invoiceId, items, onClose }: RemoveDiscountProps) => {
  const { updateInvoice, updateItem } = useBilling({ id: invoiceId })
  const discountedItems = useMemo(() => items.filter(itemHasDiscount), [items])
  const defaultSelectedIds = useMemo(() => discountedItems.map(item => item.id), [discountedItems])

  const [selectedIds, setSelectedIds] = useState<string[]>(defaultSelectedIds)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setSelectedIds(defaultSelectedIds)
  }, [defaultSelectedIds])

  const selectedItems = useMemo(
    () => discountedItems.filter(item => selectedIds.includes(item.id)),
    [discountedItems, selectedIds]
  )
  const selectedDiscountTotal = useMemo(
    () => selectedItems.reduce((sum, item) => sum + lineDiscount(item), 0),
    [selectedItems]
  )

  const toggleItem = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const toggleAll = () => {
    if (selectedIds.length === discountedItems.length) {
      setSelectedIds([])
      return
    }
    setSelectedIds(discountedItems.map(item => item.id))
  }

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      showToast('Select at least one item')
      return
    }

    setIsSubmitting(true)
    try {
      await Promise.all(
        selectedItems.map(item =>
          updateItem({
            formVal: {
              data: {
                id: item.id,
                type: 'invoice_item',
                attributes: {
                  discount_type: null,
                  discount_value: null,
                  discount_reason: null
                }
              }
            },
            id: item.id
          })
        )
      )

      await updateInvoice({
        formVal: {
          data: {
            id: invoiceId,
            type: 'invoice',
            attributes: {
              discount_type: null,
              discount_value: null,
              discount_reason: null
            }
          }
        },
        id: invoiceId
      })

      showToast(
        selectedItems.length === 1
          ? 'Discount removed from 1 item'
          : `Discount removed from ${selectedItems.length} items`
      )
      onClose()
    } catch (error) {
      getError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose} style={{ minWidth: 'min(560px, calc(100vw - 1.5rem))' }} title="Remove discount">
      <div className="flex flex-col gap-5 px-6 pb-6 pt-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-[#444]">Remove from items</span>
            <button className="text-sm font-medium text-[#1565C0] hover:underline" onClick={toggleAll} type="button">
              {selectedIds.length === discountedItems.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-[#D9D9D9]">
            {discountedItems.length === 0 ? (
              <p className="px-3 py-4 text-sm text-[#666]">No discounted items</p>
            ) : (
              discountedItems.map(item => {
                const discount = lineDiscount(item)
                const checked = selectedIds.includes(item.id)
                return (
                  <label
                    className="flex cursor-pointer items-start gap-3 border-b border-[#EEEEEE] px-3 py-2.5 last:border-b-0"
                    key={item.id}
                  >
                    <input checked={checked} className="mt-1" onChange={() => toggleItem(item.id)} type="checkbox" />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm text-[#111]">{item.attributes.name}</span>
                      <span className="text-xs capitalize text-[#777]">
                        {item.attributes.category?.replace(/_/g, ' ')}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm text-[#2E7D32]">- NGN {discount.toLocaleString()}</span>
                  </label>
                )
              })
            )}
          </div>
        </div>

        <div className="rounded-xl bg-[#F5F5F5] px-4 py-4">
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-[#555]">Discount to remove</span>
            <span className="font-medium text-[#2E7D32]">- NGN {selectedDiscountTotal.toLocaleString()}</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button kinds="outline" label="Cancel" onClick={onClose} size="sm" type="button" />
          <Button
            disabled={isSubmitting || selectedItems.length === 0}
            label="Remove discount"
            onClick={() => void handleSubmit()}
            size="sm"
            type="button"
          />
        </div>
      </div>
    </Modal>
  )
}
